import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// Bug fix: this used to build its own fetch() with a Bearer token read from
// TOKEN_KEYS.admin, which has been an empty object since the httpOnly-cookie
// migration — so it always sent no credentials and always got a 401. The
// shared `api` axios instance already sends the safer_session cookie via
// withCredentials, so route the download through it instead.
const downloadTicketPDF = async (bookingId, bookingNumber, setDlId) => {
  setDlId(bookingId);
  try {
    const res = await api.get(`/bookings/${bookingId}/ticket`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: `تذكرة-${bookingNumber}.pdf`,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    let msg = 'تعذّر تنزيل التذكرة';
    // Error responses come back as a Blob too (axios responseType is fixed
    // for the whole request) — read it back out as text to get the message.
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        msg = JSON.parse(text)?.message || msg;
      } catch {}
    }
    toast.error(msg);
  } finally {
    setDlId(null);
  }
};

const STATUS_COLORS = {
  pending_payment:   'bg-yellow-100 text-yellow-700',
  payment_submitted: 'bg-blue-100 text-blue-700',
  payment_confirmed: 'bg-indigo-100 text-indigo-700',
  payment_rejected:  'bg-red-100 text-red-600',
  ticket_issued:     'bg-green-100 text-green-700',
  cancelled:         'bg-gray-100 text-gray-500',
  completed:         'bg-green-100 text-green-700',
};

const STATUS_AR = {
  pending_payment:   'بانتظار الدفع',
  payment_submitted: 'إيصال مرفوع',
  payment_confirmed: 'دفع مؤكد',
  payment_rejected:  'إيصال مرفوض',
  ticket_issued:     'تذكرة مُصدرة ✅',
  cancelled:         'ملغي',
  completed:         'مكتمل',
};

const STATUS_FILTER_BTN = {
  '':                 'الكل',
  pending_payment:    'بانتظار الدفع',
  payment_submitted:  'إيصال مرفوع',
  ticket_issued:      'تذكرة مُصدرة ✅',
  payment_rejected:   'إيصال مرفوض',
  cancelled:          'ملغي',
};

const LIMIT = 50;

const AllBookingsPage = () => {
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [downloadingId, setDlId]  = useState(null);

  // Filters
  const [status,   setStatus]   = useState('');
  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  // Applied (submitted) values — only update on explicit search/clear
  const [appliedSearch,   setAppliedSearch]   = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo,   setAppliedDateTo]   = useState('');

  const searchRef = useRef(null);

  const fetchBookings = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: LIMIT };
      if (status)           params.status   = status;
      if (appliedSearch)    params.search   = appliedSearch;
      if (appliedDateFrom)  params.dateFrom = appliedDateFrom;
      if (appliedDateTo)    params.dateTo   = appliedDateTo;
      const res = await api.get('/users/all-bookings', { params });
      setBookings(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [status, page, appliedSearch, appliedDateFrom, appliedDateTo]);

  useEffect(() => { fetchBookings(page); }, [fetchBookings, page]);

  const handleSearch = () => {
    setAppliedSearch(search);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const handleClear = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setAppliedSearch('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setPage(1);
    searchRef.current?.focus();
  };

  const handleStatusChange = (s) => {
    setStatus(s);
    setPage(1);
  };

  const hasActiveFilters = appliedSearch || appliedDateFrom || appliedDateTo;

  return (
    <div className="p-4 md:p-6 space-y-5" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">جميع الحجوزات</h1>
        <p className="text-gray-500 text-sm">إجمالي: {total} حجز</p>
      </div>

      {/* Search + date range */}
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search input */}
          <div className="relative flex-1">
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="ابحث برقم الحجز، اسم المسافر، أو رقم الهاتف..."
              className="w-full border border-gray-200 rounded-xl pr-9 pl-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <label className="text-xs text-gray-400">من</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm text-gray-700 bg-transparent focus:outline-none w-32"
              />
            </div>
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <label className="text-xs text-gray-400">إلى</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm text-gray-700 bg-transparent focus:outline-none w-32"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              بحث
            </button>
            {hasActiveFilters && (
              <button
                onClick={handleClear}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 bg-gray-100 hover:bg-red-50 px-3 py-2.5 rounded-xl transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                مسح
              </button>
            )}
          </div>
        </div>

        {/* Active filter indicator */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {appliedSearch && <span>بحث: <strong>"{appliedSearch}"</strong>&nbsp;&nbsp;</span>}
              {(appliedDateFrom || appliedDateTo) && (
                <span>
                  الفترة:{' '}
                  {appliedDateFrom && <strong>{appliedDateFrom}</strong>}
                  {appliedDateFrom && appliedDateTo && ' — '}
                  {appliedDateTo && <strong>{appliedDateTo}</strong>}
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS_FILTER_BTN).map(([s, label]) => (
          <button
            key={s}
            onClick={() => handleStatusChange(s)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition shadow-sm ${
              status === s
                ? 'bg-blue-700 text-white shadow-blue-200'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">جاري التحميل...</div>
      ) : bookings.length === 0 ? (
        <div className="text-center bg-white rounded-2xl shadow py-20 text-gray-400">
          <div className="text-5xl mb-3">🎫</div>
          <p className="font-medium">لا توجد حجوزات</p>
          {hasActiveFilters && (
            <p className="text-sm mt-1">جرّب تعديل معايير البحث</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-right px-4 py-3 text-gray-600 font-semibold">رقم الحجز</th>
                  <th className="text-right px-4 py-3 text-gray-600 font-semibold">المسافر</th>
                  <th className="text-right px-4 py-3 text-gray-600 font-semibold">الشركة</th>
                  <th className="text-right px-4 py-3 text-gray-600 font-semibold">الرحلة</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">المبلغ</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">الحالة</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">التاريخ</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">التذكرة</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bookings.map((b) => {
                  // Bug fix: b.passenger is the linked User account, whose name
                  // can differ from (or predate) whoever was actually booked on
                  // this specific ticket. passengerDetails is the source of
                  // truth the ticket PDF already uses — mirrors BookingsPage.jsx.
                  const passengerName = b.passengerDetails?.length > 0
                    ? b.passengerDetails.map((pd) => pd.name).join('، ')
                    : b.passenger?.name || '—';
                  const passengerPhone = b.passengerDetails?.length > 0
                    ? b.passengerDetails[0]?.phone
                    : b.passenger?.phone;
                  return (
                  <tr key={b.id} className="hover:bg-gray-50/70 transition">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700">{b.bookingNumber}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{passengerName}</p>
                      <p className="text-gray-400 text-xs">{passengerPhone}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{b.company?.name}</td>
                    <td className="px-4 py-3">
                      {b.trip ? (
                        <>
                          <p className="text-gray-700">{b.trip.fromCity} → {b.trip.toCity}</p>
                          <p className="text-gray-400 text-xs">
                            {new Date(b.trip.departureTime).toLocaleDateString('ar-SA')}
                          </p>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-700">
                      {parseFloat(b.totalAmount).toFixed(0)} SDG
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[b.status]}`}>
                        {STATUS_AR[b.status] || b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-400 text-xs">
                      {new Date(b.createdAt).toLocaleDateString('ar-SA')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {['ticket_issued', 'completed'].includes(b.status) ? (
                        <button
                          onClick={() => downloadTicketPDF(b.id, b.bookingNumber, setDlId)}
                          disabled={downloadingId === b.id}
                          title="تنزيل التذكرة PDF"
                          className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 text-blue-700 border border-blue-200 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition"
                        >
                          {downloadingId === b.id ? (
                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 19h16"/>
                            </svg>
                          )}
                          PDF
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-3 border-t flex items-center justify-between text-sm text-gray-500 bg-gray-50/50">
            <span>
              عرض {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} من {total} حجز
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 transition text-xs font-medium"
              >
                السابق
              </button>
              <span className="px-3 py-1.5 text-xs font-medium text-gray-600">
                {page} / {Math.ceil(total / LIMIT) || 1}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * LIMIT >= total}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 transition text-xs font-medium"
              >
                التالي
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllBookingsPage;
